// Soft delete, shared across the modules that hold voidable records.
//
// Families and Members both carry `recordStatus`, and every screen that counts
// or lists them has to agree on what "deleted" means — otherwise a record
// vanishes from its own module but still shows up on the dashboard, in a
// printed roster, or in the birthday list.

/** Only what a soft delete is allowed to carry. */
export interface VoidableRecord {
  recordStatus?: string;
  deleteReason?: string;
  deletedAt?: string;
}

/**
 * Rows created before the column existed have `recordStatus` null, so only an
 * explicit 'VOID' counts as deleted. Testing `!== 'ACTIVE'` would hide the
 * entire existing register.
 */
export const isVoided = (record?: VoidableRecord | null): boolean =>
  record?.recordStatus === 'VOID';

/** Everything except the soft-deleted rows. */
export const liveOnly = <T extends VoidableRecord>(rows: T[] = []): T[] =>
  rows.filter((r) => !isVoided(r));

/**
 * The same rule for the financial modules, which flag deletion on `status`
 * rather than `recordStatus`.
 *
 * Accounts and Budget used to test `status === 'ACTIVE'`, which is the mistake
 * this file warns about above: a row created before the column existed, or
 * imported without one, was silently dropped from the ledger and from budget
 * actuals while still appearing in its own module. Only an explicit VOID counts.
 */
export interface StatusRecord {
  status?: string;
}

export const isVoidStatus = (record?: StatusRecord | null): boolean =>
  record?.status === 'VOID';

/** Someone has asked for this row to be deleted; an administrator has not yet decided. */
export const isDeleteRequested = (record?: StatusRecord | null): boolean =>
  record?.status === 'DELETE_REQUESTED';

/** Everything not VOID — a row awaiting a deletion decision is still live. */
export const liveByStatus = <T extends StatusRecord>(rows: T[] = []): T[] =>
  rows.filter((r) => !isVoidStatus(r));

export const voidedByStatus = <T extends StatusRecord>(rows: T[] = []): T[] =>
  rows.filter((r) => isVoidStatus(r));

export const deleteRequestedByStatus = <T extends StatusRecord>(rows: T[] = []): T[] =>
  rows.filter((r) => isDeleteRequested(r));

// --- The two-step delete the financial modules share ------------------------
//
// A financial record is never voided by the person who asks. They file a
// request with a reason; an administrator then approves it (the row becomes
// VOID and moves to Deleted History) or keeps it (the row returns to ACTIVE).
// Each step is the field set below spread over the row and PUT back, the same
// way the old one-click void was.

/** The day stamp Deleted History shows, e.g. "16 Sep 2026". */
export const auditStamp = (): string =>
  new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

export interface DeleteAudit {
  status: 'ACTIVE' | 'VOID' | 'DELETE_REQUESTED';
  deleteReason: string | null;
  deletedAt: string | null;
  deleteRequestedBy: string | null;
  deleteRequestedAt: string | null;
  deleteApprovedBy: string | null;
}

/** Step 1 — ask. The row stays live, flagged with who asked and why. */
export const requestDeletion = (reason: string, by: string): DeleteAudit => ({
  status: 'DELETE_REQUESTED',
  deleteReason: reason,
  deletedAt: null,
  deleteRequestedBy: by,
  deleteRequestedAt: auditStamp(),
  deleteApprovedBy: null
});

/** Step 2a — an administrator approves: the row is voided, the request trail kept. */
export const approveDeletion = (
  row: Pick<Partial<DeleteAudit>, 'deleteReason' | 'deleteRequestedBy' | 'deleteRequestedAt'>,
  by: string
): DeleteAudit => ({
  status: 'VOID',
  deleteReason: row.deleteReason ?? null,
  deletedAt: auditStamp(),
  deleteRequestedBy: row.deleteRequestedBy ?? null,
  deleteRequestedAt: row.deleteRequestedAt ?? null,
  deleteApprovedBy: by
});

/** Step 2b — an administrator keeps it (or a voided row is restored): back to normal, trail cleared. */
export const keepRecord = (): DeleteAudit => ({
  status: 'ACTIVE',
  deleteReason: null,
  deletedAt: null,
  deleteRequestedBy: null,
  deleteRequestedAt: null,
  deleteApprovedBy: null
});

/**
 * Next human-readable id for a prefix — nextSequence(rows, 'EXP') -> 'EXP-1007'.
 *
 * Modules keep this under different columns (`recordId`, `internalId`,
 * `receiptNo`), so each row's values are scanned for the prefix rather than one
 * field name being hard-coded here.
 *
 * Both ledger hooks used to count rows instead (`base + rows.length`). That
 * reissued a number as soon as one row was hard-deleted, and repeated the same
 * number forever if a list ever came back paged. Reading the highest number
 * already issued fixes both.
 *
 * It cannot stop two people saving in the same second from computing the same
 * value — that needs a sequence on the server.
 */
export const nextSequence = (
  rows: Array<Record<string, unknown>> = [],
  prefix: string,
  base = 1000
): string => {
  const head = `${prefix}-`;
  let highest = base - 1;

  rows.forEach((row) => {
    Object.values(row || {}).forEach((value) => {
      if (typeof value !== 'string' || !value.startsWith(head)) return;
      const tail = value.slice(head.length);
      if (!/^\d+$/.test(tail)) return;
      const issued = parseInt(tail, 10);
      if (issued > highest) highest = issued;
    });
  });

  return `${head}${highest + 1}`;
};
